import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart3, Copy, DollarSign, ExternalLink, Link2, Loader2,
  LogOut, MousePointerClick, Store, TrendingUp, Users, Download,
  Wallet, ArrowUpRight, Instagram, Facebook, MessageCircle, Sparkles, Image, Video, FileText
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";

interface Affiliate {
  id: string; user_id: string; name: string; email: string; code: string;
  commission_type: string; commission_percentage: number; commission_fixed: number;
  is_active: boolean; total_earned: number; total_paid: number;
  status: string; pix_key: string | null; pix_type: string | null;
  bio: string | null; avatar_url: string | null;
  created_at: string;
}

const AfiliadoDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { config } = usePlatformConfig();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [clicks, setClicks] = useState<any[]>([]);
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [commissionConfigs, setCommissionConfigs] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiPlatform, setAiPlatform] = useState("instagram");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState("");

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/afiliado/login"); return; }

    const { data: aff } = await supabase.from("affiliates")
      .select("*").eq("user_id", user.id).maybeSingle();

    if (!aff) { navigate("/afiliado/login"); return; }
    setAffiliate(aff as Affiliate);

    const [refsRes, clicksRes, wdRes, matsRes, configRes, plansRes] = await Promise.all([
      supabase.from("affiliate_referrals").select("*").eq("affiliate_id", aff.id).order("created_at", { ascending: false }),
      supabase.from("affiliate_clicks").select("*").eq("affiliate_id", aff.id).order("created_at", { ascending: false }),
      supabase.from("affiliate_withdrawals").select("*").eq("affiliate_id", aff.id).order("created_at", { ascending: false }),
      supabase.from("affiliate_materials").select("*").eq("is_active", true).order("sort_order"),
      supabase.from("affiliate_commission_config").select("*"),
      supabase.from("plans").select("id, name, slug, price_monthly, price_yearly").eq("is_active", true).order("sort_order"),
    ]);

    setReferrals(refsRes.data || []);
    setClicks(clicksRes.data || []);
    setWithdrawals(wdRes.data || []);
    setMaterials(matsRes.data || []);
    setCommissionConfigs(configRes.data || []);
    setPlans(plansRes.data || []);
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/afiliado/login");
  };

  const copyLink = (suffix = "") => {
    if (!affiliate) return;
    const link = `${window.location.origin}/cadastro?ref=${affiliate.code}${suffix}`;
    navigator.clipboard.writeText(link);
    toast({ title: "Link copiado!" });
  };

  const copySlugLink = () => {
    if (!affiliate) return;
    const link = `${window.location.origin}/parceiro/${affiliate.code}`;
    navigator.clipboard.writeText(link);
    toast({ title: "Link da página dedicada copiado!" });
  };

  const fmt = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

  const requestWithdraw = async () => {
    if (!affiliate) return;
    const amount = parseFloat(withdrawAmount);
    const available = affiliate.total_earned - affiliate.total_paid;
    if (!amount || amount <= 0 || amount > available) {
      toast({ title: "Valor inválido", description: `Disponível: ${fmt(available)}`, variant: "destructive" });
      return;
    }
    setWithdrawing(true);
    const { error } = await supabase.from("affiliate_withdrawals").insert({
      affiliate_id: affiliate.id,
      amount,
      pix_key: affiliate.pix_key,
      pix_type: affiliate.pix_type,
    });
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Saque solicitado!", description: "Aguarde a aprovação do administrador." });
      setWithdrawDialogOpen(false);
      setWithdrawAmount("");
      checkAuth();
    }
    setWithdrawing(false);
  };

  const generateCaption = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-marketing-content", {
        body: {
          prompt: `Gere uma legenda profissional para ${aiPlatform} sobre divulgação da plataforma ViaMenu - sistema de cardápio digital e gestão para restaurantes. Tema: ${aiPrompt}. Inclua hashtags relevantes e call-to-action.`,
          type: "caption",
        },
      });
      if (error) throw error;
      setAiResult(data?.content || "Não foi possível gerar o conteúdo.");
    } catch {
      toast({ title: "Erro ao gerar conteúdo", variant: "destructive" });
    }
    setAiLoading(false);
  };

  // Chart data
  const clicksByDay = clicks.reduce((acc: any, c) => {
    const day = new Date(c.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    acc[day] = (acc[day] || 0) + 1;
    return acc;
  }, {});
  const clickChartData = Object.entries(clicksByDay).slice(-14).map(([day, count]) => ({ day, cliques: count }));

  const referralsByMonth = referrals.reduce((acc: any, r) => {
    const m = new Date(r.created_at).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
    acc[m] = (acc[m] || 0) + 1;
    return acc;
  }, {});
  const referralChartData = Object.entries(referralsByMonth).map(([month, count]) => ({ month, conversões: count }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!affiliate) return null;

  const availableBalance = affiliate.total_earned - affiliate.total_paid;
  const pendingWithdrawals = withdrawals.filter(w => w.status === "pending").reduce((s, w) => s + w.amount, 0);
  const activeReferrals = referrals.filter(r => r.status === "active").length;
  const conversionRate = clicks.length > 0 ? ((referrals.length / clicks.length) * 100).toFixed(1) : "0";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-card/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {config.logo_url ? (
              <img src={config.logo_url} alt={config.logo_text} className="w-9 h-9 rounded-xl object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-xl bg-gradient-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs">{config.logo_initials}</span>
              </div>
            )}
            <div>
              <h1 className="text-sm font-bold font-display">{affiliate.name}</h1>
              <p className="text-[10px] text-muted-foreground">Código: {affiliate.code}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Sair
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: "Saldo Disponível", value: fmt(availableBalance), icon: Wallet, color: "text-green-500" },
            { label: "Total Ganho", value: fmt(affiliate.total_earned), icon: DollarSign, color: "text-primary" },
            { label: "Restaurantes Ativos", value: activeReferrals, icon: Store, color: "text-blue-500" },
            { label: "Total de Cliques", value: clicks.length, icon: MousePointerClick, color: "text-orange-500" },
            { label: "Taxa de Conversão", value: `${conversionRate}%`, icon: TrendingUp, color: "text-purple-500" },
          ].map(k => (
            <div key={k.label} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <k.icon className={`h-4 w-4 ${k.color}`} />
                <p className="text-[10px] text-muted-foreground">{k.label}</p>
              </div>
              <p className="text-xl font-bold font-display">{k.value}</p>
            </div>
          ))}
        </div>

        {/* Links */}
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <h2 className="font-display font-semibold text-sm flex items-center gap-2">
            <Link2 className="h-4 w-4 text-primary" /> Seus Links de Indicação
          </h2>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/50">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-muted-foreground">Link de Cadastro</p>
                <p className="text-xs font-mono truncate">{window.location.origin}/cadastro?ref={affiliate.code}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => copyLink()}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/50">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-muted-foreground">Página Dedicada</p>
                <p className="text-xs font-mono truncate">{window.location.origin}/parceiro/{affiliate.code}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={copySlugLink}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => copyLink("&utm_source=instagram&utm_medium=bio")}>
              <Instagram className="h-3.5 w-3.5 mr-1" /> Instagram
            </Button>
            <Button size="sm" variant="outline" onClick={() => copyLink("&utm_source=facebook&utm_medium=post")}>
              <Facebook className="h-3.5 w-3.5 mr-1" /> Facebook
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              const text = encodeURIComponent(`Conheça o ViaMenu! O melhor sistema de cardápio digital para restaurantes. Cadastre-se: ${window.location.origin}/cadastro?ref=${affiliate.code}&utm_source=whatsapp`);
              window.open(`https://wa.me/?text=${text}`, "_blank");
            }}>
              <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-secondary/50 w-full justify-start overflow-x-auto">
            <TabsTrigger value="overview" className="text-xs">Visão Geral</TabsTrigger>
            <TabsTrigger value="referrals" className="text-xs">Indicações</TabsTrigger>
            <TabsTrigger value="financial" className="text-xs">Financeiro</TabsTrigger>
            <TabsTrigger value="marketing" className="text-xs">Marketing</TabsTrigger>
            <TabsTrigger value="materials" className="text-xs">Materiais</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-display font-semibold mb-3">Cliques (últimos 14 dias)</h3>
                {clickChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={clickChartData}>
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="cliques" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-muted-foreground text-center py-8">Nenhum clique registrado</p>}
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-display font-semibold mb-3">Conversões por Mês</h3>
                {referralChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={referralChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="conversões" stroke="hsl(var(--primary))" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-muted-foreground text-center py-8">Nenhuma conversão</p>}
              </div>
            </div>

            {/* Commission config */}
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-sm font-display font-semibold mb-3">Comissões por Plano</h3>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {plans.map(plan => {
                  const config = commissionConfigs.find((c: any) => c.plan_id === plan.id);
                  return (
                    <div key={plan.id} className="p-3 rounded-lg bg-secondary/50">
                      <p className="text-xs font-medium">{plan.name}</p>
                      <p className="text-lg font-bold font-display">
                        {config ? `${config.commission_percentage}%` : "—"}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {plan.price_monthly === 0 ? "Gratuito (sem comissão)" :
                          config?.commission_type === "recurring" ? "Recorrente" : "Única"}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Referrals */}
          <TabsContent value="referrals" className="space-y-4">
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {referrals.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Store className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nenhuma indicação ainda</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-muted-foreground border-b border-border">
                      <th className="text-left p-4 font-medium">Data</th>
                      <th className="text-left p-4 font-medium">Status</th>
                      <th className="text-left p-4 font-medium">Comissão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map(r => (
                      <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/30">
                        <td className="p-4 text-sm">{new Date(r.created_at).toLocaleDateString("pt-BR")}</td>
                        <td className="p-4">
                          <Badge variant={r.status === "active" ? "default" : "secondary"} className="text-[10px]">
                            {r.status === "active" ? "Ativo" : r.status === "pending" ? "Pendente" : r.status}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm font-medium">{fmt(r.commission_earned)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* Financial */}
          <TabsContent value="financial" className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-[10px] text-muted-foreground">Disponível para Saque</p>
                <p className="text-xl font-bold font-display text-green-500">{fmt(availableBalance - pendingWithdrawals)}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-[10px] text-muted-foreground">Saques Pendentes</p>
                <p className="text-xl font-bold font-display text-orange-500">{fmt(pendingWithdrawals)}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-[10px] text-muted-foreground">Total Recebido</p>
                <p className="text-xl font-bold font-display">{fmt(affiliate.total_paid)}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <Button onClick={() => setWithdrawDialogOpen(true)} className="w-full h-full bg-gradient-primary text-primary-foreground" disabled={availableBalance - pendingWithdrawals <= 0}>
                  <ArrowUpRight className="h-5 w-5 mr-2" /> Solicitar Saque
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <h3 className="text-sm font-display font-semibold p-4 border-b border-border">Histórico de Saques</h3>
              {withdrawals.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center p-8">Nenhum saque solicitado</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-muted-foreground border-b border-border">
                      <th className="text-left p-4 font-medium">Data</th>
                      <th className="text-left p-4 font-medium">Valor</th>
                      <th className="text-left p-4 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map(w => (
                      <tr key={w.id} className="border-b border-border/50">
                        <td className="p-4 text-sm">{new Date(w.created_at).toLocaleDateString("pt-BR")}</td>
                        <td className="p-4 text-sm font-medium">{fmt(w.amount)}</td>
                        <td className="p-4">
                          <Badge variant={w.status === "paid" ? "default" : w.status === "approved" ? "default" : w.status === "rejected" ? "destructive" : "secondary"} className="text-[10px]">
                            {w.status === "pending" ? "Pendente" : w.status === "approved" ? "Aprovado" : w.status === "paid" ? "Pago" : "Rejeitado"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* Marketing AI */}
          <TabsContent value="marketing" className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
              <h3 className="text-sm font-display font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" /> Gerador de Legendas com IA
              </h3>
              <div className="flex gap-2">
                {[
                  { val: "instagram", icon: Instagram, label: "Instagram" },
                  { val: "facebook", icon: Facebook, label: "Facebook" },
                  { val: "whatsapp", icon: MessageCircle, label: "WhatsApp" },
                ].map(p => (
                  <Button key={p.val} size="sm" variant={aiPlatform === p.val ? "default" : "outline"}
                    onClick={() => setAiPlatform(p.val)}>
                    <p.icon className="h-3.5 w-3.5 mr-1" /> {p.label}
                  </Button>
                ))}
              </div>
              <Textarea
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                placeholder="Descreva o tema da postagem... Ex: 'Benefícios do cardápio digital para restaurantes'"
                rows={3}
              />
              <Button onClick={generateCaption} disabled={aiLoading || !aiPrompt.trim()} className="bg-gradient-primary text-primary-foreground">
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Gerar Legenda
              </Button>
              {aiResult && (
                <div className="p-4 rounded-lg bg-secondary/50 space-y-2">
                  <p className="text-sm whitespace-pre-wrap">{aiResult}</p>
                  <Button size="sm" variant="ghost" onClick={() => {
                    navigator.clipboard.writeText(aiResult);
                    toast({ title: "Copiado!" });
                  }}>
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copiar
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Materials */}
          <TabsContent value="materials" className="space-y-4">
            {materials.length === 0 ? (
              <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
                <Image className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nenhum material disponível ainda</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {materials.map(m => (
                  <div key={m.id} className="rounded-xl border border-border bg-card overflow-hidden">
                    {m.type === "image" && m.file_url && (
                      <img src={m.file_url} alt={m.title} className="w-full h-40 object-cover" />
                    )}
                    {m.type === "video" && m.file_url && (
                      <div className="w-full h-40 bg-secondary flex items-center justify-center">
                        <Video className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    <div className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        {m.type === "image" && <Image className="h-3.5 w-3.5 text-muted-foreground" />}
                        {m.type === "video" && <Video className="h-3.5 w-3.5 text-muted-foreground" />}
                        {m.type === "text" && <FileText className="h-3.5 w-3.5 text-muted-foreground" />}
                        <p className="text-sm font-medium">{m.title}</p>
                      </div>
                      {m.description && <p className="text-xs text-muted-foreground">{m.description}</p>}
                      {m.content && <p className="text-xs bg-secondary/50 p-2 rounded">{m.content}</p>}
                      <div className="flex gap-2">
                        {m.file_url && (
                          <Button size="sm" variant="outline" asChild>
                            <a href={m.file_url} download target="_blank" rel="noopener noreferrer">
                              <Download className="h-3.5 w-3.5 mr-1" /> Baixar
                            </a>
                          </Button>
                        )}
                        {m.content && (
                          <Button size="sm" variant="ghost" onClick={() => {
                            navigator.clipboard.writeText(m.content);
                            toast({ title: "Texto copiado!" });
                          }}>
                            <Copy className="h-3.5 w-3.5 mr-1" /> Copiar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Solicitar Saque</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Disponível: <strong>{fmt(availableBalance - pendingWithdrawals)}</strong></p>
            <div className="space-y-1.5">
              <Label className="text-sm">Valor (R$)</Label>
              <Input type="number" min="0" step="0.01" value={withdrawAmount}
                onChange={e => setWithdrawAmount(e.target.value)} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Chave PIX</Label>
              <Input value={affiliate.pix_key || ""} disabled className="h-10 text-sm" />
              <p className="text-[10px] text-muted-foreground">Tipo: {affiliate.pix_type || "Não informado"}</p>
            </div>
            <Button onClick={requestWithdraw} disabled={withdrawing} className="w-full bg-gradient-primary text-primary-foreground">
              {withdrawing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowUpRight className="h-4 w-4 mr-2" />}
              Solicitar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AfiliadoDashboard;
